import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import CardMembershipIcon from '@mui/icons-material/CardMembership'; // Icon for packages
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn'; // Icon for deactivate
import PeopleIcon from '@mui/icons-material/People'; // Icon for Live Agent
import CompanySubscriptionManager from './components/CompanySubscriptionManager'; // Import the new component

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userPackageAssignments, setUserPackageAssignments] = useState([]);
  const [loadingPackagesInfo, setLoadingPackagesInfo] = useState(true); // Combined loading for user's packages & available packages
  const [packagesError, setPackagesError] = useState(null);

  // State for Assign Package Dialog
  const [openAssignPackageDialog, setOpenAssignPackageDialog] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageToAssign, setSelectedPackageToAssign] = useState('');
  const [isAssigningPackage, setIsAssigningPackage] = useState(false);
  const [assignPackageError, setAssignPackageError] = useState(null);

  const [isDeactivatingPackage, setIsDeactivatingPackage] = useState(false);
  const [deactivatePackageError, setDeactivatePackageError] = useState(null);

  // --- NEW: State for Subscription Manager ---
  const [openSubscriptionManager, setOpenSubscriptionManager] = useState(false);
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const [saveSubscriptionError, setSaveSubscriptionError] = useState(null);
  // --- END NEW ---

  // --- NEW: State for Live Agent Dialog ---
  const [openLiveAgentDialog, setOpenLiveAgentDialog] = useState(false);
  // --- END NEW ---

  // --- NEW: State for company subscriptions (both AI and Live Agent) ---
  const [companySubscription, setCompanySubscription] = useState(null); // AI subscription (backward compat)
  const [liveAgentSubscription, setLiveAgentSubscription] = useState(null); // Live Agent subscription
  const [quotas, setQuotas] = useState(null); // Company quotas for seat usage
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  // --- END NEW ---

  // Admin detection (platform admin via app_metadata)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  const getAuthToken = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }, []);

  const fetchAvailablePackages = useCallback(async (token, anonKey) => {
    const response = await fetch(`/functions/v1/admin-package-manager/packages`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `Failed to fetch available packages: ${response.status}`);
    }
    const result = await response.json();
    return result.packages || [];
  }, []);

  const fetchCompanySubscription = useCallback(async (companyId) => {
    if (!companyId) return null;
    try {
      // Fetch AI subscription, Live Agent subscription, and quotas in parallel
      const [{ data: aiSub }, { data: laSub }, { data: quota }] = await Promise.all([
        supabase
          .from('company_subscriptions')
          .select(`
            *, 
            packages ( name ),
            stripe_coupon_id,
            stripe_promotion_code,
            coupon_name,
            coupon_percent_off,
            coupon_amount_off,
            coupon_duration,
            coupon_duration_months,
            discount_amount,
            coupon_applied_at
          `)
          .eq('company_id', companyId)
          .in('status', ['active', 'trialing'])
          .maybeSingle(),
        supabase
          .from('company_live_agent_subscriptions')
          .select(`
            *,
            stripe_coupon_id,
            stripe_promotion_code,
            coupon_name,
            coupon_percent_off,
            coupon_amount_off,
            coupon_duration,
            coupon_duration_months,
            discount_amount,
            coupon_applied_at
          `)
          .eq('company_id', companyId)
          .eq('status', 'active')
          .maybeSingle(),
        supabase
          .from('company_quotas')
          .select('live_agent_seat_limit, live_agent_seats_used')
          .eq('company_id', companyId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle()
      ]);

      // Store Live Agent subscription and quotas
      setLiveAgentSubscription(laSub || null);
      setQuotas(quota || null);

      // Return AI subscription for backward compatibility
      return aiSub;
    } catch (err) {
      console.error("Error fetching company subscriptions:", err);
      setPackagesError("Failed to load company subscription details.");
      return null;
    }
  }, []);

  const fetchCompanyByAdmin = useCallback(async (adminUserId) => {
    if (!adminUserId) return null;
    try {
      // If platform admin, try admin endpoint which can read any user's company
      if (isPlatformAdmin) {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
        const resp = await fetch(`/functions/v1/admin-user-manager/users/${adminUserId}/company`, {
          headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey }
        });
        if (resp.ok) {
          const body = await resp.json();
          // If multiple companies are returned, pick the first for now
          const cid = body?.company?.company_id || body?.companies?.[0]?.company_id;
          if (cid) return { company_id: cid };
        }
        // fall back to client-side query
      }

      const { data, error } = await supabase
        .from('companies')
        .select('company_id')
        .eq('admin_user_id', adminUserId)
        .maybeSingle();

      if (error) {
        console.warn("Could not find company for admin user:", error.message);
        return null;
      }
      return data;
    } catch (err) {
      console.error("Error fetching company by admin:", err);
      return null;
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!userId) {
        setError("User ID not provided in URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setPackagesError(null);
      setUser(null);
      setCompanySubscription(null);

      try {
        const token = await getAuthToken();
        if (!token) throw new Error("Auth token not available.");
        const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
        if (!anonKey) throw new Error("Supabase anon key is not configured.");

        // Step 0: determine platform admin
        try {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          const adminFlag = authUser?.app_metadata?.app_role === 'admin' || authUser?.raw_app_meta_data?.app_role === 'admin';
          setIsPlatformAdmin(!!adminFlag);
        } catch { }

        // Step 1: Fetch User Details first
        const userDetailsResponse = await fetch(`/functions/v1/admin-user-manager/users/${userId}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey }
        });
        if (!userDetailsResponse.ok) {
          const errData = await userDetailsResponse.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to fetch user details.");
        }
        const fetchedUserData = await userDetailsResponse.json();
        const userObject = fetchedUserData?.user || fetchedUserData;
        if (!userObject?.id) {
          throw new Error("User data not found or in an unexpected format.");
        }
        setUser(userObject);

        // Step 2: Fetch the user's company
        const companyData = await fetchCompanyByAdmin(userObject.id);

        // Step 3: Fetch the company subscription if a company was found
        if (companyData?.company_id) {
          const subscriptionData = await fetchCompanySubscription(companyData.company_id);
          setCompanySubscription(subscriptionData);
        }

        // Step 4: Fetch available packages
        const packagesData = await fetchAvailablePackages(token, anonKey);
        setAvailablePackages(packagesData || []);

      } catch (err) {
        console.error("Error fetching page data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
        setLoadingSubscription(false);
        setLoadingPackagesInfo(false);
      }
    };
    fetchAllData();
  }, [userId, getAuthToken, fetchCompanySubscription, fetchAvailablePackages, fetchCompanyByAdmin]);

  const handleOpenAssignPackageDialog = () => {
    setSelectedPackageToAssign(''); // Reset selection
    setAssignPackageError(null);
    setOpenAssignPackageDialog(true);
    // Available packages are already fetched on page load
  };

  const handleCloseAssignPackageDialog = () => {
    setOpenAssignPackageDialog(false);
  };

  const handleAssignPackage = async () => {
    if (!selectedPackageToAssign) {
      setAssignPackageError("Please select a package to assign.");
      return;
    }
    setIsAssigningPackage(true);
    setAssignPackageError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Auth token not available.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key not configured.");

      const payload = {
        user_id: userId,
        package_id: selectedPackageToAssign,
        // start_date will be set to now() by default in DB or by Edge function
        // end_date can be null by default for ongoing
        is_active: true // New assignments are active
      };

      // We'll call the POST endpoint of the admin-user-package-manager function
      const response = await fetch(`/functions/v1/admin-user-package-manager/${userId}`, { // Or a dedicated /assign endpoint
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Failed to assign package: ${response.status} ${result.message}`);
      }
      // Refetch the new company subscription instead of the old user package assignment
      const companyData = await fetchCompanyByAdmin(user.id);
      if (companyData?.company_id) {
        const updatedSubscription = await fetchCompanySubscription(companyData.company_id);
        setCompanySubscription(updatedSubscription);
      }
      handleCloseAssignPackageDialog();

    } catch (err) {
      console.error("Error assigning package:", err);
      setAssignPackageError(err.message);
    } finally {
      setIsAssigningPackage(false);
    }
  };

  const handleSaveSubscription = async (payload) => {
    if (!companySubscription) {
      setSaveSubscriptionError("No active subscription found to update.");
      return;
    }

    console.log(`[UserDetailPage] Saving subscription ${companySubscription.subscription_id} with payload:`, payload);
    setIsSavingSubscription(true);
    setSaveSubscriptionError(null);

    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Authentication token not available.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key is not configured.");

      // Endpoint should target company subscriptions now
      const response = await fetch(`/functions/v1/admin-package-manager/subscription/${companySubscription.subscription_id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || `Failed to update subscription: ${response.status}`);

      // Refetch subscription to see the changes
      const companyData = await fetchCompanyByAdmin(user.id);
      if (companyData?.company_id) {
        const updatedSubscription = await fetchCompanySubscription(companyData.company_id);
        setCompanySubscription(updatedSubscription);
      }
      setOpenSubscriptionManager(false);

    } catch (err) {
      console.error("Error saving subscription:", err);
      setSaveSubscriptionError(err.message);
      // Re-throw to be caught by the component if needed, or just display error
      throw err;
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const handleDeactivatePackage = async (userPackageId) => {
    if (!window.confirm("Are you sure you want to deactivate this user's current package? This will set its end date to now.")) {
      return;
    }
    setIsDeactivatingPackage(true);
    setDeactivatePackageError(null);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error("Auth token not available.");
      const anonKey = (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__.REACT_APP_SUPABASE_ANON_KEY) || process.env.REACT_APP_SUPABASE_ANON_KEY;
      if (!anonKey) throw new Error("Supabase anon key not configured.");

      // We'll use a PUT request to a new sub-path for deactivation for clarity
      // Or add a specific action in the body for the POST handler
      // Let's assume a PUT to /functions/v1/admin-user-package-manager/{userId}/assignment/{userPackageId}/deactivate
      // For now, we will make a PUT request to the user_package_id directly
      // The Edge function needs to be updated to handle this PUT request to deactivate.

      const response = await fetch(`/functions/v1/admin-user-package-manager/${userId}/deactivate-package`, {
        method: 'POST', // Using POST for simplicity, could be PUT to a more specific endpoint
        headers: { 'Authorization': `Bearer ${token}`, 'apikey': anonKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_package_id: userPackageId }) // Send ID of assignment to deactivate
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || `Failed to deactivate package: ${response.status} ${result.message}`);
      }
      // Refetch the company subscription to show the change
      const companyData = await fetchCompanyByAdmin(user.id);
      if (companyData?.company_id) {
        const updatedSubscription = await fetchCompanySubscription(companyData.company_id);
        setCompanySubscription(updatedSubscription);
      }
    } catch (err) {
      console.error("Error deactivating package:", err);
      setDeactivatePackageError(err.message);
      // Show error to user e.g. via Snackbar or Alert
    } finally {
      setIsDeactivatingPackage(false);
    }
  };

  if (loading) {
    return <Container><Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box></Container>;
  }

  if (error) {
    return <Container><Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></Container>;
  }

  if (!user) {
    // This condition will now primarily be hit if fetchAllData completed but resulted in user being null
    // (e.g., if the explicit error for user not found wasn't thrown or was caught differently)
    // Or if an error occurred before user could be set, and `error` state is also set.
    return <Container><Alert severity="warning" sx={{ mt: 2 }}>User data could not be loaded or user not found.</Alert></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
          Admin Panel
        </Link>
        <Typography color="text.primary">User Details</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>
        User Details: {user.email}
      </Typography>

      <Grid container spacing={3}>
        {/* User Info Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center' }}>
              <AccountBoxIcon sx={{ mr: 1 }} /> General Information
            </Typography>
            <List dense>
              <ListItem><ListItemText primary="User ID:" secondary={user.id} /></ListItem>
              <ListItem><ListItemText primary="Email:" secondary={user.email} /></ListItem>
              <ListItem><ListItemText primary="Role:" secondary={user.role || (user.user_metadata?.role) || 'user'} /></ListItem>
              <ListItem><ListItemText primary="App Role (for admin access):" secondary={user.app_metadata?.app_role || 'N/A'} /></ListItem>
              <ListItem><ListItemText primary="Created At:" secondary={new Date(user.created_at).toLocaleString()} /></ListItem>
              <ListItem><ListItemText primary="Last Sign In:" secondary={user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleString() : 'Never'} /></ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Quick Actions & Package Info Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" gutterBottom component="div" sx={{ display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} /> Actions & Status
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>Current Package:</Typography>
              {loadingSubscription && <CircularProgress size={20} />}
              {packagesError && <Alert severity="error" size="small">{packagesError}</Alert>}
              {deactivatePackageError && <Alert severity="error" size="small" sx={{ mt: 1 }}>{`Deactivation failed: ${deactivatePackageError}`}</Alert>}
              {!loadingSubscription && !packagesError && (
                <Box>
                  {/* AI Package */}
                  {companySubscription ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                        <Chip
                          icon={<CardMembershipIcon />}
                          label={`AI: ${companySubscription.packages.name} (Active)`}
                          color="success"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Button
                          variant="outlined"
                          color="warning"
                          size="small"
                          startIcon={<DoNotDisturbOnIcon />}
                          onClick={() => handleDeactivatePackage(companySubscription.subscription_id)}
                          disabled={isDeactivatingPackage}
                          sx={{ mb: 1 }}
                        >
                          {isDeactivatingPackage ? <CircularProgress size={18} /> : 'Deactivate'}
                        </Button>
                      </Box>

                      {/* Coupon Display */}
                      {companySubscription.stripe_coupon_id && (
                        <Box sx={{ mt: 1, mb: 1, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 0.5 }}>
                            🎉 Coupon: {companySubscription.stripe_promotion_code || companySubscription.stripe_coupon_id}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {companySubscription.coupon_percent_off
                              ? `${companySubscription.coupon_percent_off}% off`
                              : companySubscription.coupon_amount_off
                                ? `$${(companySubscription.coupon_amount_off / 100).toFixed(2)} off`
                                : ''
                            }
                            {companySubscription.coupon_duration === 'forever' && ' (Forever)'}
                            {companySubscription.coupon_duration === 'once' && ' (One-time)'}
                            {companySubscription.coupon_duration === 'repeating' &&
                              ` (${companySubscription.coupon_duration_months} months)`
                            }
                          </Typography>
                          {companySubscription.discount_amount && (
                            <Typography variant="caption" display="block" sx={{ color: '#2e7d32' }}>
                              Saves ${companySubscription.discount_amount.toFixed(2)}/month
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">No active AI package.</Typography>
                  )}

                  {/* Live Agent */}
                  {liveAgentSubscription && (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1 }}>
                        <Chip
                          icon={<PeopleIcon />}
                          label={`Live Agent: ${liveAgentSubscription.seat_count} ${liveAgentSubscription.seat_count === 1 ? 'Seat' : 'Seats'}`}
                          color="secondary"
                          sx={{ mr: 1, mb: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          ${(liveAgentSubscription.seat_count * 19).toFixed(2)}/month
                        </Typography>
                      </Box>

                      {/* Live Agent Coupon Display */}
                      {liveAgentSubscription.stripe_coupon_id && (
                        <Box sx={{ mt: 1, mb: 1, p: 1.5, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold', mb: 0.5 }}>
                            🎉 Coupon: {liveAgentSubscription.stripe_promotion_code || liveAgentSubscription.stripe_coupon_id}
                          </Typography>
                          <Typography variant="caption" display="block">
                            {liveAgentSubscription.coupon_percent_off
                              ? `${liveAgentSubscription.coupon_percent_off}% off`
                              : liveAgentSubscription.coupon_amount_off
                                ? `$${(liveAgentSubscription.coupon_amount_off / 100).toFixed(2)} off`
                                : ''
                            }
                            {liveAgentSubscription.coupon_duration === 'forever' && ' (Forever)'}
                            {liveAgentSubscription.coupon_duration === 'once' && ' (One-time)'}
                            {liveAgentSubscription.coupon_duration === 'repeating' &&
                              ` (${liveAgentSubscription.coupon_duration_months} months)`
                            }
                          </Typography>
                          {liveAgentSubscription.discount_amount && (
                            <Typography variant="caption" display="block" sx={{ color: '#2e7d32' }}>
                              Saves ${liveAgentSubscription.discount_amount.toFixed(2)}/month
                            </Typography>
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}
              <Button variant="contained" size="small" onClick={handleOpenAssignPackageDialog} sx={{ mt: 1 }}>
                Assign / Change Package
              </Button>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Button component={RouterLink} to={`/admin/user/${userId}/files`} variant="outlined" startIcon={<DescriptionIcon />} sx={{ mb: 1 }}>
              Manage User Files
            </Button>
            <Button component={RouterLink} to={`/admin/user/${userId}/session-settings`} variant="outlined" startIcon={<VpnKeyIcon />} sx={{ mb: 1 }}>
              Manage Session & Chat & Memory Settings
            </Button>
            <Button component={RouterLink} to={`/admin/user/${userId}/default-settings`} variant="outlined" startIcon={<SettingsIcon />} sx={{ mb: 1 }}>
              Manage Default User settings
            </Button>
            <Button component={RouterLink} to={`/admin/user/${userId}/integrations`} variant="outlined" startIcon={<SettingsIcon />} sx={{ mb: 1 }}>
              Manage Marketplace Integrations
            </Button>
            {/* Always show to platform admins; for others, show only if subscription exists */}
            {(isPlatformAdmin || !!companySubscription) && (
              <Button onClick={() => setOpenSubscriptionManager(true)} variant="outlined" startIcon={<CardMembershipIcon />} sx={{ mb: 1 }}>
                Manage AI Package Settings
              </Button>
            )}

            {/* Live Agent Subscription Management */}
            <Button
              onClick={() => setOpenLiveAgentDialog(true)}
              variant="outlined"
              startIcon={<PeopleIcon />}
              sx={{
                mb: 1,
                borderColor: liveAgentSubscription ? undefined : 'error.main',
                color: liveAgentSubscription ? undefined : 'error.main',
                '&:hover': {
                  borderColor: liveAgentSubscription ? undefined : 'error.dark',
                  bgcolor: liveAgentSubscription ? undefined : 'error.light',
                }
              }}
            >
              {liveAgentSubscription ? 'Manage Live Agent Subscription' : 'No Live Agent Subscription'}
            </Button>

            {/* Add other quick actions if needed */}
          </Paper>
        </Grid>

        {/* Package Assignment History section removed - 
            Company subscriptions are now shown prominently in the 
            "Current Package" section above with full details */}


      </Grid>

      {/* Assign Package Dialog */}
      <Dialog open={openAssignPackageDialog} onClose={handleCloseAssignPackageDialog} fullWidth maxWidth="xs">
        <DialogTitle>Assign Package to {user?.email}</DialogTitle>
        <DialogContent>
          {assignPackageError && <Alert severity="error" sx={{ mb: 2 }}>{assignPackageError}</Alert>}
          {loadingPackagesInfo ? (
            <CircularProgress />
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="assign-package-select-label">Select Package</InputLabel>
              <Select
                labelId="assign-package-select-label"
                value={selectedPackageToAssign}
                label="Select Package"
                onChange={(e) => setSelectedPackageToAssign(e.target.value)}
              >
                <MenuItem value=""><em>Select a package</em></MenuItem>
                {availablePackages.map((pkg) => (
                  <MenuItem key={pkg.id} value={pkg.id}>
                    {pkg.name} (${pkg.price_usd.toFixed(2)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseAssignPackageDialog}>Cancel</Button>
          <Button onClick={handleAssignPackage} variant="contained" disabled={isAssigningPackage || loadingPackagesInfo || !selectedPackageToAssign}>
            {isAssigningPackage ? <CircularProgress size={24} /> : 'Assign Package'}
          </Button>
        </DialogActions>
      </Dialog>

      <CompanySubscriptionManager
        open={openSubscriptionManager}
        onClose={() => setOpenSubscriptionManager(false)}
        subscription={companySubscription}
        onSave={handleSaveSubscription}
        companyName={user?.company_name || user?.email}
      />

      {/* Live Agent Subscription Dialog */}
      <Dialog
        open={openLiveAgentDialog}
        onClose={() => setOpenLiveAgentDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Live Human Agent Subscription
          {liveAgentSubscription ? (
            <Chip label="Active" color="success" size="small" sx={{ ml: 2 }} />
          ) : (
            <Chip label="Not Subscribed" color="error" size="small" sx={{ ml: 2 }} />
          )}
        </DialogTitle>
        <DialogContent>
          {liveAgentSubscription ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Seat Count</Typography>
                    <Typography variant="h4">
                      {liveAgentSubscription.seat_count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {liveAgentSubscription.seat_count === 1 ? 'seat' : 'seats'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Monthly Cost</Typography>
                    <Typography variant="h4">
                      ${(liveAgentSubscription.seat_count * 19).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      $19 per seat
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Seats Assigned</Typography>
                    <Typography variant="h4">
                      {quotas?.live_agent_seats_used || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      of {liveAgentSubscription.seat_count} used
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Available Seats</Typography>
                    <Typography variant="h4">
                      {liveAgentSubscription.seat_count - (quotas?.live_agent_seats_used || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      seats available
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Subscription Details</Typography>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Typography variant="body1">{liveAgentSubscription.status}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Cancels at Period End</Typography>
                    <Typography variant="body1">
                      {liveAgentSubscription.cancel_at_period_end ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Current Period Start</Typography>
                    <Typography variant="body1">
                      {liveAgentSubscription.current_period_start
                        ? new Date(liveAgentSubscription.current_period_start).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Current Period End</Typography>
                    <Typography variant="body1">
                      {liveAgentSubscription.current_period_end
                        ? new Date(liveAgentSubscription.current_period_end).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Stripe Subscription ID</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {liveAgentSubscription.stripe_subscription_id}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Agent Assignments</Typography>
              <Alert severity="info" sx={{ mt: 1 }}>
                To manage agent seat assignments, use the Stripe Customer Portal or contact support.
              </Alert>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom color="error">
                No Live Agent Subscription
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                This company has not subscribed to Live Human Agent support.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Live Agent provides live human support via WhatsApp, Web Chat, Instagram, Messenger, and Telegram.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => {
                  setOpenLiveAgentDialog(false);
                  window.open('/upgrade', '_blank');
                }}
              >
                Subscribe to Live Agent
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLiveAgentDialog(false)}>Close</Button>
          {liveAgentSubscription && (
            <Button
              variant="outlined"
              onClick={() => {
                window.open('/billing', '_blank');
              }}
            >
              Manage in Billing Page
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default UserDetailPage;